import { useTranslation } from 'next-i18next';

import { Badge, BadgeProps, Flex } from '@chakra-ui/react';

import { IconProps, IconsLinear, IconsTwoTone } from '@nocowanie/icons';

import { accommodationStaticSlice, useAppSelector } from '@app/state';

import { AccommodationBadgeType } from './accommodation-badge-type';
import { AccommodationBadgesProps } from './accommodation-badges.props';

export const AccommodationBadges = ({ badges, iconProps, ...props }: AccommodationBadgesProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'basicInformation',
    });

    const isBestseller = useAppSelector(accommodationStaticSlice.selectors.selectIsBestseller);
    const isRealTime = badges.includes(AccommodationBadgeType.InstantReservation);
    const iconPropsCommon: IconProps = {
        fontSize: '1.5em',
        mt: '-1px',
        mb: 0,
        ...iconProps,
    };
    const iconStyleCommon = { '--icon-stroke': 1, scale: 0.95 };
    const badgePropsCommon: BadgeProps = {
        borderColor: 'gray.900',
        borderWidth: '1px',
        borderRadius: 'sm',
        boxShadow: 'none',
        fontSize: 'xs',
        fontWeight: 'normal',
        py: 0,
    };

    return (
        <Flex
            flexDirection={'row'}
            gap={2}
            flexWrap={{
                base: 'wrap',
                md: 'nowrap',
            }}
            alignItems={'center'}
            {...props}
        >
            {isRealTime ? (
                <Badge variant={'solid'} colorPalette={'green'} {...badgePropsCommon}>
                    <IconsLinear.Flash
                        color={'gray.900'}
                        fill={'orange.600'}
                        ml={-0.5}
                        {...iconPropsCommon}
                        css={iconStyleCommon}
                    />
                    {t('badges.instantReservation')}
                </Badge>
            ) : null}
            {badges.includes(AccommodationBadgeType.ConfirmationUpTo24Hours) ? (
                <Badge
                    variant={'outline'}
                    colorPalette={'gray'}
                    bg={'green.50'}
                    {...badgePropsCommon}
                >
                    <IconsTwoTone.Clock2
                        fill={'orange.solid'}
                        {...iconPropsCommon}
                        css={iconStyleCommon}
                    />
                    {t('badges.confirmationUpTo24Hours')}
                </Badge>
            ) : null}
            {badges.includes(AccommodationBadgeType.PromotedOnTV) ? (
                <Badge
                    variant={'solid'}
                    colorPalette="yellow"
                    fontWeight={'normal'}
                    {...badgePropsCommon}
                >
                    <IconsLinear.PlayCircle {...iconPropsCommon} />
                    {t('badges.promotedOnTV')}
                </Badge>
            ) : null}
            {isBestseller ? (
                <Badge
                    data-testid="bestseller"
                    variant={'outline'}
                    colorPalette={'gray'}
                    {...badgePropsCommon}
                >
                    <IconsLinear.Like1
                        accentFillColor={'orange.solid'}
                        {...iconPropsCommon}
                        css={iconStyleCommon}
                    />
                    {t('badges.bestseller')}
                </Badge>
            ) : null}
            {badges.includes(AccommodationBadgeType.NewAccommodation) ? (
                <Badge
                    data-testid="no-rating"
                    variant={'subtle'}
                    colorPalette="secondary"
                    {...badgePropsCommon}
                    ml={{
                        md: 'auto',
                    }}
                >
                    {t('badges.newAccommodation')}
                </Badge>
            ) : null}
        </Flex>
    );
};
